import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { formatTextEllipsis, formatValueWithCompactNumber } from '@/lib/format';
import { cn } from '@/lib/utils';
import { useEffect, useMemo, useState } from 'react';
import { useBoomWallet } from '@boomeme/wallet-sdk';
import { IInvite } from '@/request/referral/referral.response.interface';
import AccountAvatar from '@/widget/AccountAvatar';
import referralApiService from '@/request/referral/referral.api.service';
import { Trans, useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { useAppEventBusContext } from '@/context/eventBusContext';
import { SubscribeEvent } from '@/interface/event';
import CopyNode from '@/widget/CopyNode';
import { useAppStore } from '@/store';
import CustomToolTips from '@/widget/CustomToolTips';
import { Question } from '@/widget/Icon';
import { useTheme } from '@/components/ThemeProvider';
import NoData from '@/widget/NoData';
import BigNumber from 'bignumber.js';
import Login from '../account/components/Login';

export default () => {
  const {
    fwClass,
    lang,
    profile,
    chainInfo: { path },
    setProfile,
  } = useAppStore();
  const { t } = useTranslation();
  const { isConnected, walletAddress } = useBoomWallet();
  const [inviteList, setInviteList] = useState<IInvite[]>([]);
  const { publish } = useAppEventBusContext();
  const account = walletAddress?.toLocaleLowerCase();
  const { theme } = useTheme();

  const handle401Error = async (fn: () => Promise<any>) => {
    try {
      const res = await fn();
      if (res?.code === 401) {
        publish(SubscribeEvent.Sign_Expired, true);
        return null;
      } else {
        return res;
      }
    } catch (error) {
      return null;
    }
  };

  const fetchProfile = async () => {
    try {
      const profile = await referralApiService.getUserInfo();
      if (profile) {
        setProfile(profile);
      }
    } catch (error) {
      console.log(error);
    }
  };

  const fetchData = async () => {
    if (!account || !isConnected) return;
    if (!localStorage.getItem(account)) return publish(SubscribeEvent.Sign_Expired, true);
    const invite = await handle401Error(() => referralApiService.getInviteList({ page: 1, limit: 100 }));
    if (invite == null) return;
    if (invite?.items?.length > 0) {
      setInviteList(invite.items);
    }
  };

  useEffect(() => {
    if (isConnected && account) {
      fetchData();
    }
  }, [isConnected, walletAddress, account]);

  const link = `https://boom.meme/#/${path}?inviteCode=${profile?.invite_code}`;

  const withdraw = async () => {
    try {
      await referralApiService.withdrawReward();
      await fetchProfile();
    } catch (error) {
      console.log(error);
    }
  };

  const ReferralCard = useMemo(() => {
    return () => (
      <div className="w-full h-[184px] relative rounded-[12px] overflow-hidden p-[12px]">
        <img src="/icon/referral-bg.png" className="absolute w-full inset-0 z-0 dark:bg-white bg-[#18181B]" />
        <div className=" relative z-10 text-white dark:text-[#18181B]">
          <div className={cn('text-18px mb-[16px]', fwClass)}>
            <Trans i18nKey="Referral.referralText" components={[<span className={cn('text-[20px] text-themeColor', fwClass)} />]} />
          </div>
          <p className="text-[12px]  mb-[6px]">{t('Referral.invitationLink')}</p>
          <div className="dark:bg-[#09090B] bg-[#fff] flex items-center justify-between px-[12px] py-[8px] rounded-[8px]">
            <span className={cn('dark:text-white text-[#18181B]', fwClass)}>{link}</span>
            <CopyNode text={link} />
          </div>
        </div>
      </div>
    );
  }, [link, lang]);

  const ReferralOverview = useMemo(() => {
    const point = BigNumber(profile?.trade_amount || 0)
      .times(100)
      .toString();
    const isGt50 = BigNumber(profile?.reward_account?.total_amount || 0).gte(50);
    return () => (
      <div>
        <h4 className={cn('text-[16px] mb-[8px]', fwClass)}>{t('Referral.referralOverview')}</h4>
        <div className="flex flex-col gap-y-[12px]">
          <div className="grid grid-cols-2 gap-x-[13px] gap-y-[20px]">
            <div className="text-[12px] ">
              <p className="text-secondaryTextColor mb-[9px] flex items-center gap-x-[6px]">
                {t('Referral.availableRewards')}
                <CustomToolTips
                  wrapperClass="p-0"
                  triggerNode={<Question theme={theme} className="size-[16px]" />}
                  content={
                    <div className="w-[160px] p-[8px] bg-primaryBgColor text-primaryTextColor border-[1px] border-borderColor rounded">
                      {t('Referral.withdrawTips')}
                    </div>
                  }
                />
              </p>
              <p className="flex gap-x-[6px]">
                <span className={cn('text-[18px]', fwClass)}>{formatValueWithCompactNumber(profile?.reward_account?.total_amount, true)}</span>
                <Button
                  disabled={!isGt50}
                  className={cn('!text-themeColor !bg-transparent h-[26px] border-[1px] p-[8px] !border-themeColor', !isGt50 && 'opacity-[0.6]')}
                  onClick={withdraw}
                >
                  {t('Referral.withdraw')}
                </Button>
              </p>
            </div>
            <div className="text-[12px] ">
              <p className="text-secondaryTextColor mb-[9px]">{t('Referral.claimedRewards')}</p>
              <p>
                <span className={cn('text-[18px]', fwClass)}>{formatValueWithCompactNumber(profile?.reward_account?.claimed_amount)}</span>
              </p>
            </div>
            <div className="text-[12px] ">
              <p className="text-secondaryTextColor mb-[9px]">{t('Referral.totalReferrals')}</p>
              <p>
                <span className={cn('text-[18px]', fwClass)}>{profile?.referrer_counts || 0}</span>
              </p>
            </div>
            <div className="text-[12px] ">
              <p className="text-secondaryTextColor mb-[9px] flex items-center gap-x-[6px]">
                {t('Referral.points')}
                <CustomToolTips
                  wrapperClass="p-0"
                  triggerNode={<Question theme={theme} className="size-[16px]" />}
                  content={
                    <div className="w-[160px] p-[8px] bg-primaryBgColor text-primaryTextColor border-[1px] border-borderColor rounded">
                      {t('Referral.pointTips')}
                    </div>
                  }
                />
              </p>
              <div className="flex items-center gap-x-[10px]">
                <p>
                  <span className={cn('text-[18px]', fwClass)}>{formatValueWithCompactNumber(point)}</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }, [lang, profile, theme]);

  const MasterProgram = useMemo(() => {
    return () => (
      <div>
        <p className={cn(fwClass, 'mb-[8px]')}>{t('Referral.ambassadorProgram')}</p>
        <div className="px-[12px] rounded-[12px] flex items-center justify-between gap-x-[20px] bg-[#F4F4F580] dark:bg-[#27272A80]">
          <div className="text-[14px]">{t('Referral.ambassadorProgramTips')}</div>
          <Button
            className={cn('!bg-themeColor', fwClass)}
            onClick={() => {
              window.open('https://t.me/boommeme_service');
            }}
          >
            {t('Referral.apply')}
          </Button>
        </div>
      </div>
    );
  }, [lang]);

  const ReferralList = useMemo(() => {
    return () => (
      <div>
        <p className={cn('mb-[8px]', fwClass)}>{t('Referral.myReferrals')}</p>
        {inviteList.length > 0 ? (
          <Table>
            <TableHeader>
              <TableRow className="text-secondaryTextColor border-b-[1px] border-borderColor !bg-transparent">
                <TableHead className="w-[120px] px-0">{t('Referral.wallet')}</TableHead>
                <TableHead className="w-[118px] px-0 text-right">{t('Referral.volume')}</TableHead>
                <TableHead className="w-[118px] px-0 text-right">{t('Referral.rewards')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {inviteList.map((item) => {
                const volume = BigNumber(item.trade_amount).gt(0) ? formatValueWithCompactNumber(item.trade_amount) : '--';
                const reward = BigNumber(item.referrer_volume).gt(0) ? formatValueWithCompactNumber(item.referrer_volume) : '--';
                return (
                  <TableRow className="text-[12px] h-[64px] !border-b-[1px] border-borderColor !bg-transparent" key={item.account}>
                    <TableCell className="text-left">
                      <div className="flex items-center gap-x-[12px]">
                        <AccountAvatar walletAddress={item.account} className="w-[30px] h-[30px]" />
                        <span className="text-[14px]">{formatTextEllipsis(item.account, 4, 3)}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-right px-0">{volume}</TableCell>
                    <TableCell className="text-right px-0">{reward}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : (
          <NoData />
        )}
      </div>
    );
  }, [lang, inviteList]);

  return isConnected ? (
    <div className="flex flex-col px-[16px] gap-y-[20px]">
      <ReferralCard />
      <ReferralOverview />
      <MasterProgram />
      <ReferralList />
    </div>
  ) : (
    <Login />
  );
};
